<template>
  <v-container class="pa-0">
    <h1 class="display-1">{{ title }}</h1>
    <v-breadcrumbs v-if="breadcrumb" :items="breadcrumb" class="pl-0">
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item
          :disabled="isLastItem(item)"
          @click="goToParent(item)"
        >
          {{ item.name }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>
    <v-row justify="center" v-if="documents.length > 0">
      <v-col cols="12">
        <h2 class="title font-weight-light" v-if="subtitle">{{ subtitle }}</h2>
      </v-col>
      <v-col cols="12">
        <v-row dense>
          <v-col cols="12" md="6" v-if="documents.length === 0 && videos.length === 0">
            <span>{{ $t('library.messages.no-results') }}</span>
          </v-col>
          <v-col
            v-for="document in documents"
            :key="document.id"
            :cols="document.cols"
          >
            <v-card :loading="document.loading" height="100%">
              <v-img
                :aspect-ratio="document.aspect_ratio"
                @click="loadDocument(document)"
                :src="document.image_url"
                class="white--text align-end"
                gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.7)"
                height="200px"
              >
                <v-card-title class="text-truncate">
                  <span class="text-truncate">{{document.name}}</span>
                </v-card-title>
              </v-img>
              <v-card-text v-if="document.description">{{ document.description }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row justify="center" v-if="videos && videos.length > 0">
      <v-col cols="12">
        <v-row>
          <v-col cols="12">
            <h3 class="font-weight-light headline">{{ $t('library.subtitles.videos') }}</h3>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            v-for="video in videos"
            :key="video.id"
            :cols="3"
          >
            <video-card :video="video"></video-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-overlay :value="!loaded" opacity="1" color="white" light absolute>
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<style lang="scss">

.v-breadcrumbs__item {
  cursor: pointer;
  color: #1E88E5;
}

</style>

<script>

import { getUrlImage } from '@/store/storage'
import { getDocumentsFromCollection, getCategoriesByComponent, getCategoriesByCategory, getVideosByCategory } from '@/store/module/library'
import VideoCardComponent from '@/components/VideoCard'

export default {
  components: {
    'video-card': VideoCardComponent
  },
  data: () => ({
    loaded: false,
    documents: [],
    videos: [],
    title: '',
    subtitle: '',
    breadcrumb: []
  }),

  mounted() {
    this.title = this.$t('library.titles.main')
    this.getList()
  },

  methods: {
    getList(parent) {
      let type = 'components'
      getDocumentsFromCollection(type)
      .then(documents => {
        this.breadcrumb = []
        this.breadcrumb.push({ name: this.$t('library.breadcrumb.main'), main: true })
        this.subtitle_documents = this.$t('library.subtitles.components')
        this.fillDocuments(documents, parent, type)
      })
      .catch(error => {
        console.log('Error in Library', error);
      })
    },
    fillVideos(videos) {
      this.videos = videos
    },
    fillDocuments(documents, parent, type) {
      this.documents = []
      let cols = this.getColsByType(type)
      let aspect_ratio = this.getAspectRatioByType(type)
      for (let document of documents) {
        document.parent = parent
        document.loading = true
        document.type = type
        getUrlImage(document.image).then(url => {
          document.image_url = url
          document.loading = false
        })
        document.cols = cols
        document.aspect_ratio = aspect_ratio
        this.documents.push(document)
      }

      // Check navigation
      if (this.$route.params.resource) {
        let document_navigation = this.documents.find(d => d.id === this.$route.params.resource.id)
        if (document_navigation) {
          this.loadDocument(document_navigation)
        }
      }

      this.loaded = true
    },
    loadDocument(document) {
      this.subtitle = this.$t('library.titles.by-type', { name: document.name })
      if (document.type === 'components') {
        this.videos = []
        this.loadCategoriesByComponent(document)
      } else if (document.type === 'categories') {
        this.videos = []
        this.loadCategoriesByCategory(document)
        this.loadVideosByCategory(document)
      }
    },
    getColsByType(type) {
      let cols = 4
      switch (type) {
        case 'components':
          cols = 3
          break
        case 'categories':
          cols = 2
          break
        case 'videos':
        default:
          cols = 3
          break
      }

      return cols
    },
    getAspectRatioByType(type) {
      let ratio = "1/1"
      switch (type) {
        case 'components':
          ratio = "4/3"
          break
        case 'categories':
          ratio = "1/1"
          break
        case 'videos':
        default:
          ratio = "16/9/3"
          break
      }

      return ratio
    },
    loadCategoriesByComponent(document) {
      getCategoriesByComponent(document)
      .then(documents => {
        this.breadcrumb.push({ name: document.name, document: document })
        this.fillDocuments(documents, document, 'categories')
      })
      .catch(error => {
        console.log('Error in Library', error.message);
      })
    },
    loadCategoriesByCategory(document) {
      getCategoriesByCategory(document)
      .then(documents => {
        this.breadcrumb.push({ name: document.name, document: document })
        this.fillDocuments(documents, document, 'categories')
      })
      .catch(error => {
        console.log('Error in Library', error.message);
      })
    },
    loadVideosByCategory(document) {
      getVideosByCategory(document)
      .then(videos => {
        this.fillVideos(videos)
      })
      .catch(error => {
        console.log('Error in Library', error.message);
      })
    },
    isLastItem(item) {
      return this.breadcrumb[this.breadcrumb.length - 1] === item
    },
    goToParent(item) {
      if (item.main) {
        this.title = this.$t('library.titles.main')
        this.getList()
      } else {
        this.breadcrumb = this.breadcrumb.slice(0, this.breadcrumb.indexOf(item))
        this.loadDocument(item.document)
      }
    }
  }
}

</script>
